/**
 * EO with a virtual examiner: microphone test, then for each task the examiner reads the
 * instructions (French voice), the preparation time (task 2), then the recording with a strict
 * countdown that cuts at the end (« Merci, c'est terminé. »). Each recording is uploaded before
 * the next task; the learner can listen to their own recordings at the end of the section.
 * Task 2: the examiner answers each turn live (liveExaminer.ts), read aloud and shown.
 */

import { academyAttemptsNextCreate, academyAttemptsRecordingCreate } from "@agencyos/api-client";
import { Button } from "@agencyos/ui-web";
import { Ear, MessageSquareReply, Mic, MicOff, Square, UserRound } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  closeStream,
  extensionFor,
  openMicrophone,
  recorderMimeType,
  recorderOptions,
  sendRecording,
  speakFrench,
  stopSpeaking,
  useMicLevel, useMicStayedSilent, 
  useSilence,
} from "./audio";
import { AudioReplay } from "./ListeningReplay";
import { useLiveExaminer } from "./liveExaminer";
import { usePhaseCountdown } from "./shared";
import { clock } from "./text";
import type { PlayerState, StateTask } from "./types";
import { Ring } from "./ui";
import { OralCreditsNotice } from "./TrainerReview";

type Phase = "mic" | "ready" | "examiner" | "prep" | "record" | "upload" | "uploadError" | "between" | "review";

interface Recording {
  taskNumber: number;
  url: string;
}

const BARS = [0.35, 0.6, 0.85, 1, 0.75, 0.5, 0.9, 0.65, 0.4, 0.8, 0.55, 0.3];

/** Microphone level as a row of bars (a meter for assistive technologies). */
export function LevelMeter({ level, label }: { level: number; label: string }) {
  const value = Math.max(0, Math.min(1, level));
  return (
    <div className={`ac-voice${value > 0.15 ? " ac-voice--ok" : ""}`}>
      <div
        className="ac-voice__bars"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(value * 100)}
      >
        {BARS.map((weight, index) => (
          <span key={index} style={{ transform: `scaleY(${0.12 + Math.min(1, value * 1.6) * weight * 0.88})` }} />
        ))}
      </div>
      <span className="ac-caption">{label}</span>
    </div>
  );
}

/** The examiner: a calm avatar that pulses while the French voice speaks. */
export function Examiner({ speaking, label }: { speaking: boolean; label: string }) {
  return (
    <div className={`ac-examiner${speaking ? " ac-examiner--speaking" : ""}`}>
      <span className="ac-examiner__avatar" aria-hidden="true">
        <UserRound size={30} strokeWidth={1.6} />
      </span>
      <span className="ac-caption">{label}</span>
    </div>
  );
}

export function SpeakingSection({
  attemptId,
  state,
  onChanged,
}: {
  attemptId: string;
  state: PlayerState;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [micError, setMicError] = useState(false);
  const [phase, setPhase] = useState<Phase>("mic");
  const [done, setDone] = useState<string[]>(() => state.tasks.filter((task) => task.has_audio).map((task) => task.id));
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [finishing, setFinishing] = useState(false);
  const [followUp, setFollowUp] = useState("");
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const pendingBlob = useRef<Blob | null>(null);
  const followIndex = useRef(0);
  const level = useMicLevel(stream);
  const micSilent = useMicStayedSilent(level, phase === "record");

  const task: StateTask | undefined = state.tasks.find((item) => !done.includes(item.id));
  const prepSeconds = task?.limits.prep_seconds ?? 0;
  const speakSeconds = task?.limits.seconds ?? 0;

  useEffect(
    () => () => {
      stopSpeaking();
      if (recorder.current?.state === "recording") recorder.current.stop();
    },
    [],
  );
  useEffect(() => () => closeStream(stream), [stream]);
  // The last task is over: the microphone is released at once (D-114), not when leaving.
  useEffect(() => {
    if (phase === "review") setStream(null);
  }, [phase]);
  const urls = useRef<string[]>([]);
  useEffect(() => {
    const created = urls.current;
    return () => created.forEach((url) => URL.revokeObjectURL(url)); // when leaving the section
  }, []);

  const testMicrophone = async () => {
    setMicError(false);
    try {
      setStream(await openMicrophone());
      setPhase("ready");
    } catch {
      setMicError(true);
    }
  };

  const upload = useCallback(
    async (blob: Blob, current: StateTask) => {
      setPhase("upload");
      const type = blob.type || "audio/webm";
      const file = new File([blob], `eo-tache-${current.number}.${extensionFor(type)}`, { type });
      try {
        await sendRecording(() => academyAttemptsRecordingCreate(attemptId, { task: current.id, audio: file }));
        pendingBlob.current = null;
        const url = URL.createObjectURL(blob);
        urls.current.push(url);
        setRecordings((list) => [...list, { taskNumber: current.number, url }]);
        setDone((list) => [...list, current.id]);
        setPhase("between");
      } catch {
        pendingBlob.current = blob;
        setPhase("uploadError");
      }
    },
    [attemptId],
  );

  const startRecording = useCallback(() => {
    if (!stream || !task) return;
    const mimeType = recorderMimeType();
    const media = new MediaRecorder(stream, recorderOptions(mimeType));
    chunks.current = [];
    media.ondataavailable = (event) => {
      if (event.data.size) chunks.current.push(event.data);
    };
    const current = task;
    media.onstop = () => {
      const blob = new Blob(chunks.current, { type: media.mimeType || mimeType || "audio/webm" });
      void speakFrench(t("academy.examinerFr.end"));
      void upload(blob, current);
    };
    recorder.current = media;
    media.start(1000);
    followIndex.current = 0;
    setFollowUp("");
    setPhase("record");
  }, [stream, task, t, upload]);

  const stopRecording = useCallback(() => {
    if (recorder.current?.state === "recording") recorder.current.stop();
  }, []);

  const beginTask = useCallback(async () => {
    if (!task) return;
    setPhase("examiner");
    await speakFrench(`${t("academy.examinerFr.task", { n: task.number })} ${task.prompt}`);
    if (prepSeconds > 0) setPhase("prep");
    else startRecording();
  }, [task, prepSeconds, startRecording, t]);

  // After an uploaded task, a short pause, then the examiner goes on with the next one.
  useEffect(() => {
    if (phase !== "between") return;
    const timer = window.setTimeout(() => {
      if (task) void beginTask();
      else setPhase("review");
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [phase, task, beginTask]);

  const prepLeft = usePhaseCountdown(prepSeconds, phase === "prep", () => {
    void speakFrench(t("academy.examinerFr.start")).then(startRecording);
  });
  const speakLeft = usePhaseCountdown(speakSeconds, phase === "record", stopRecording);
  // Task 2: live examiner; the server says when the task ends (end of its time).
  const live = useLiveExaminer({
    attemptId,
    task,
    stream,
    level,
    active: phase === "record" && Boolean(task?.live),
    onEnd: stopRecording,
  });

  // When the learner stays silent, the examiner relaunches with neutral follow-ups: task 1 is
  // an interview led by the examiner, task 3 a point of view he relaunches (FEI manual, D-134);
  // task 2 without the live examiner.
  const followUps = task?.follow_ups ?? [];
  useSilence(level, phase === "record" && Boolean(task) && !task?.live && followUps.length > 0, 8, () => {
    const line = followUps[followIndex.current % followUps.length] ?? "";
    followIndex.current += 1;
    setFollowUp(line);
    void speakFrench(line);
  });

  const finish = async () => {
    setFinishing(true);
    try {
      await academyAttemptsNextCreate(attemptId);
    } finally {
      await onChanged();
      setFinishing(false);
    }
  };

  const noRecorder = typeof MediaRecorder === "undefined";

  const phaseTotal = phase === "prep" ? prepSeconds : speakSeconds;
  const phaseLeft = phase === "prep" ? prepLeft : speakLeft;

  return (
    <section className="ac-speaking" aria-label={t("academy.skill.eo")}>
      <div className="ac-question__sheet ac-speaking__sheet">
        <OralCreditsNotice />
        {phase === "mic" && (
          <div className="ac-stage">
            <span className="ac-stage__mic" aria-hidden="true">
              <Mic size={34} strokeWidth={1.8} />
            </span>
            <h2 className="ac-title-2">{t("academy.speaking.micTitle")}</h2>
            <p className="ac-secondary ac-stage__text">{t("academy.speaking.micHint")}</p>
            {noRecorder ? (
              <p className="ko">{t("academy.speaking.noRecorder")}</p>
            ) : (
              <Button size="lg" icon={<Mic aria-hidden size={18} />} onClick={() => void testMicrophone()}>
                {t("academy.speaking.micTest")}
              </Button>
            )}
            {micError && (
              <p className="ko ac-status" role="alert">
                <MicOff aria-hidden size={18} />
                {t("academy.speaking.micDenied")}
              </p>
            )}
          </div>
        )}

        {phase === "ready" && (
          <div className="ac-stage">
            <h2 className="ac-title-2">{t("academy.speaking.micTitle")}</h2>
            <LevelMeter level={level} label={t("academy.speaking.level")} />
            <p className="ac-secondary ac-stage__text">{t("academy.speaking.speakNow")}</p>
            <ul className="ac-eo-tasks">
              {state.tasks.map((item) => (
                <li key={item.id}>
                  <span className="ac-task-tab__n ao-num" aria-hidden="true">
                    {item.number}
                  </span>
                  <span>{t("academy.speaking.task", { n: item.number, total: state.tasks.length })}</span>
                  <span className="ac-caption ao-num">{clock(item.limits.seconds ?? 0)}</span>
                </li>
              ))}
            </ul>
            <p className="ac-footnote ac-stage__text">{t("academy.speaking.rules")}</p>
            {task ? (
              <Button size="lg" onClick={() => void beginTask()}>
                {t("academy.speaking.start")}
              </Button>
            ) : (
              <Button size="lg" onClick={() => setPhase("review")}>
                {t("academy.player.continue")}
              </Button>
            )}
          </div>
        )}

        {task && (phase === "examiner" || phase === "prep" || phase === "record") && (
          <div className="ac-stage ac-stage--task">
            <p className="ac-player__eyebrow">{t("academy.speaking.task", { n: task.number, total: state.tasks.length })}</p>
            <p className="ac-prompt ac-prompt--center" lang="fr">
              {task.prompt}
            </p>
            {phase === "examiner" && (
              <div role="status" className="ac-stage__status">
                <Examiner speaking label={t("academy.speaking.examinerSpeaks")} />
              </div>
            )}
            {(phase === "prep" || phase === "record") && (
              <div className={`ac-countdown ac-countdown--${phase}${phaseLeft <= 10 ? " ac-countdown--end" : ""}`}>
                <Ring ratio={phaseTotal ? phaseLeft / phaseTotal : 0} size={196} stroke={12} tone={phase === "record" ? "eo" : "accent"}>
                  <span className="ac-countdown__state">
                    {phase === "record" ? (
                      <>
                        <span className="ac-rec-dot" aria-hidden="true" />
                        <span role="status">{t("academy.speaking.recording")}</span>
                      </>
                    ) : (
                      t("academy.speaking.prep")
                    )}
                  </span>
                  <span
                    className="ac-countdown__digits ao-num"
                    role="timer"
                    aria-label={phase === "prep" ? t("academy.speaking.prep") : t("academy.speaking.timeLeft")}
                  >
                    {clock(phaseLeft)}
                  </span>
                </Ring>
              </div>
            )}
            {phase === "prep" && <p className="ac-secondary ac-stage__text">{t("academy.speaking.prepHint")}</p>}
{micSilent && <p className="ac-note ac-note--warning" role="alert">{t("academy.redo.micSilent")}</p>}
            {phase === "record" && (
              <>
                <LevelMeter level={level} label={t("academy.speaking.level")} />
                {task.live && (
                  <div className="ac-live-examiner" aria-live="polite">
                    <p className="ac-status ac-secondary" role="status">
                      {live.status !== "listening" && <span className="ac-dot ac-dot--busy" aria-hidden="true" />}
                      {t(`academyExaminer.status.${live.status}`)}
                    </p>
                    <p className="ac-footnote">{t("academyExaminer.hint")}</p>
                    {live.offline && <p className="ac-footnote">{t("academyExaminer.offline")}</p>}
                    {live.lines.length > 0 && (
                      <ul className="ac-transcript" aria-label={t("academyExaminer.conversation")}>
                        {live.lines.map((line, index) => (
                          <li key={`${index}-${line.role}`} className={`ac-bubble ac-bubble--${line.role === "examiner" ? "them" : "me"}`}>
                            <strong className="ac-bubble__who">{t(`academyExaminer.role.${line.role}`)}</strong>{" "}
                            <span lang="fr">{line.text}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Button
                      icon={<MessageSquareReply aria-hidden size={16} />}
                      onClick={live.finishTurn}
                      disabled={live.status !== "listening"}
                    >
                      {t("academyExaminer.finishedSentence")}
                    </Button>
                  </div>
                )}
                {task.number === 2 && !task.live && (
                  <p className="ac-status ac-secondary">
                    <Ear aria-hidden size={18} />
                    {t("academy.speaking.listens")}
                  </p>
                )}
                {followUp && (
                  <p className="ac-bubble ac-bubble--them" lang="fr">
                    {followUp}
                  </p>
                )}
                <Button variant="ghost" icon={<Square aria-hidden size={14} />} onClick={stopRecording}>
                  {t("academy.speaking.stopEarly")}
                </Button>
              </>
            )}
          </div>
        )}

        {phase === "upload" && (
          <div className="ac-stage">
            <p className="ac-status" role="status">
              <span className="ac-dot ac-dot--busy" aria-hidden="true" />
              {t("academy.speaking.uploading")}
            </p>
          </div>
        )}

        {phase === "uploadError" && task && (
          <div className="ac-stage" role="alert">
            <p className="ko">{t("academy.speaking.uploadError")}</p>
            <Button onClick={() => pendingBlob.current && void upload(pendingBlob.current, task)}>{t("common.retry")}</Button>
          </div>
        )}

        {phase === "between" && (
          <div className="ac-stage">
            <p className="ac-status ac-status--success" role="status">
              <span lang="fr">{t("academy.examinerFr.end")}</span>
            </p>
          </div>
        )}

        {phase === "review" && (
          <div className="ac-stage ac-stage--review">
            <h2 className="ac-title-2">{t("academy.speaking.reviewTitle")}</h2>
            {recordings.length ? (
              <ul className="ac-recordings">
                {recordings.map((rec) => (
                  <li key={rec.url}>
                    <span className="ac-headline">{t("academy.speaking.recordingOf", { n: rec.taskNumber })}</span>
                    <AudioReplay url={rec.url} label={t("academy.speaking.recordingOf", { n: rec.taskNumber })} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="ac-secondary">{t("academy.speaking.noLocalRecording")}</p>
            )}
            <Button size="lg" onClick={() => void finish()} disabled={finishing}>
              {finishing ? t("common.loading") : t("academy.speaking.finish")}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
